<template>
    <div>
        <app-bar :title="collection.name"/>

        <v-layout>
            <v-progress-linear
                v-if="overlay"
                absolute
                indeterminate
            ></v-progress-linear>

            <div class="pt-8 pb-12 px-8" style="width: 100%">
                <div class="pb-10">
                    <v-btn
                        @click="edit"
                        class="sm mr-5"
                        color="primary"
                    >{{ t('general_action_edit') }}
                    </v-btn>
                    <v-btn
                        class="sm mr-5"
                        color="primary"
                        outlined
                        @click.prevent="suspend(collection)"
                    >
                        {{ collection.enabled ? t('general_action_suspend') : t('general_action_unsuspend') }}
                    </v-btn>
                    <confirm-modal
                        v-if="permitted('admin_workout_management')"
                        :title="t('general_title_confirm_action')"
                        @agree="hideWorkouts(collection)"
                    >
                        <template #content>
                            <div class="text-center">{{ t('collection_warning_hide_workouts') }}</div>
                        </template>
                        <template #default="{ on, attrs }">
                            <v-btn
                                class="sm mr-5"
                                color="primary"
                                outlined
                                :disabled="inLibrary === 0"
                                v-on="on"
                                v-bind="attrs"
                            >{{ t('collection_action_hide_workouts') }}</v-btn>
                        </template>
                    </confirm-modal>
                    <confirm-modal
                        v-if="permitted('admin_workout_management')"
                        :title="t('general_title_confirm_action')"
                        @agree="showWorkouts(collection)"
                    >
                        <template #content>
                            <div class="text-center">{{ t('collection_warning_show_workouts') }}</div>
                        </template>
                        <template #default="{ on, attrs }">
                            <v-btn
                                class="sm mr-5"
                                color="primary"
                                outlined
                                :disabled="restorable === 0"
                                v-on="on"
                                v-bind="attrs"
                            >{{ t('collection_action_show_workouts') }}</v-btn>
                        </template>
                    </confirm-modal>
                    <confirm-modal
                        :title="t('general_title_confirm_action')"
                        @agree="del(collection)"
                    >
                        <template #content>
                            <div class="text-center">{{ t('collection_warning_delete') }}</div>
                        </template>
                        <template #default="{ on, attrs }">
                            <v-btn
                                class="sm"
                                color="orange"
                                outlined
                                v-on="on"
                                v-bind="attrs"
                            >{{ t('general_action_delete') }}</v-btn>
                        </template>
                    </confirm-modal>
                </div>
                <div class="float-left mr-10" style="width: 394px; max-width: 394px">
                    <img
                        v-if="collection.photoUrl"
                        :src="collection.photoUrl"
                        width="394"
                    />
                    <img
                        v-if="collection.headerPhotoUrl"
                        class="mt-4"
                        :src="collection.headerPhotoUrl"
                        width="394"
                    />
                    <div class="form-subtitle">{{ t('collection_label_description') }}:</div>
                    <div class="form-subtitle-description" v-html="collection.description"></div>
                    <template v-if="collection.author">
                        <div class="form-subtitle">{{ t('collection_label_author') }}:</div>
                        <div class="form-subtitle-description">{{ collection.author }}</div>
                    </template>
                    <template v-if="collection.authorPhotoUrl">
                        <div class="form-subtitle">{{ t('collection_label_author_photo') }}:</div>
                        <v-avatar size="92" class="mt-2">
                            <v-img :src="collection.authorPhotoUrl"></v-img>
                        </v-avatar>
                    </template>
                    <template v-if="collection.videoUrl">
                        <div class="form-subtitle">{{ t('collection_label_video_url') }}:</div>
                        <div class="form-subtitle-description">
                            <a :href="collection.videoUrl" target="_blank" rel="noopener">{{ collection.videoUrl }}</a>
                        </div>
                    </template>
                    <mux-playback-id-field
                        readonly
                        :value="collection.muxPlaybackId"
                        :label="t('collection_label_mux_playback_id')"
                    ></mux-playback-id-field>

                    <card-unit class="mt-6" :items="items()">
                        <template #title>{{ t('collection_label_id') }}: {{ collection.id }}</template>
                    </card-unit>
                </div>
                <div class="float-left" style="max-width: calc(100% - 434px)">
                    <card-collection
                        :meta="workoutsMeta"
                        :busy="workoutsBusy"
                        @updated="onWorkoutsPage"
                    >
                        <template #data>
                            <thead>
                            <tr>
                                <th>{{ t('workout_label_id') }}</th>
                                <th>{{ t('workout_label_name') }}</th>
                                <th>{{ t('workout_label_stroke') }}</th>
                                <th>{{ t('collection_label_level') }}</th>
                                <th>{{ t('workout_label_duration') }}</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-if="workouts.data.length === 0">
                                <td class="text-center" colspan="5">{{ t('general_text_no_data') }}</td>
                            </tr>
                            <tr v-for="workout in workouts.data">
                                <td class="titled">{{ workout.id }}</td>
                                <td class="titled">
                                    <a
                                        :href="`/workouts/${workout.id}/view`"
                                        target="_blank"
                                    >{{ workout.name || `#${workout.id}` }}</a>
                                    <v-icon
                                        v-if="workout.ai || ! permitted('admin_workout_management')"
                                        v-show="workout.libraryHidden"
                                        :title="workout.ai ? t('workout_hint_library_hidden_ai') : t('workout_label_library_hidden')"
                                        class="ml-2"
                                        color="orange"
                                        small
                                    >mdi-eye-off-outline</v-icon>
                                    <v-icon
                                        v-else
                                        :title="workout.libraryHidden ? t('workout_action_show_in_library') : t('workout_action_hide_from_library')"
                                        class="ml-2 touched"
                                        :color="workout.libraryHidden ? 'orange' : 'grey lighten-1'"
                                        small
                                        @click="toggleLibraryHidden(workout)"
                                    >{{ workout.libraryHidden ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon>
                                </td>
                                <td>{{ workout.stroke.name }}</td>
                                <td>{{ levelTitle(workout.level) }}</td>
                                <td>{{ time.format(workout.workoutTotalTime) }}</td>
                            </tr>
                            </tbody>
                        </template>
                    </card-collection>
                </div>
            </div>
        </v-layout>
    </div>
</template>

<script lang="ts">
    import { computed, defineComponent, onMounted, reactive, toRefs } from '@vue/composition-api'
    import { useI18n } from 'vue-i18n-composable'
    import { useStore } from '@/store'
    import { useRouter } from '@/composition-api'
    import { useEnums, useStrokes } from '@/core/defaults'
    import { useWorkoutCollectionHttp } from '@/core/api/workout-collections'
    import { useWorkoutHttp } from '@/core/api/workouts'
    import { Workout } from '@/core/models/workout'
    import { WorkoutCollectionModel } from '@/core/models/workout-collection'
    import { WorkoutCollection as WorkoutList } from '@/store/modules/workout-collection'
    import * as time from '@/core/utils/time-helper'
    import ConfirmModal from '@/components/ConfirmModal.vue'
    import CardCollection from '@/components/CardCollection.vue'
    import CardUnit from '@/components/CardUnit.vue'
    import MuxPlaybackIdField from '@/components/MuxPlaybackIdField.vue'

    export default defineComponent({
        components: {
            MuxPlaybackIdField,
            ConfirmModal,
            CardCollection,
            CardUnit
        },

        setup(props) {
            const store = useStore()
            const router = useRouter()
            const i18n = useI18n()
            const enums = useEnums()
            const strokeEnums = useStrokes()
            const id = router.currentRoute.params['id']

            const state = reactive({
                overlay: true,
                workoutsBusy: true,
                workoutsMeta: undefined,
                // Workouts of the collection still shown in the app library; 0 disables "Hide workouts".
                // null until counted — the button stays usable rather than blocked on a failed count.
                inLibrary: <number | null>null,
                // Hidden workouts that "Show workouts" would bring back (AI sessions never come back); 0 disables it.
                restorable: <number | null>null,
            })

            // Collection's workouts are paginated through the Workout API filtered by collection_id.
            const workoutQueries = reactive({
                collection_id: id,
                page: 1,
                per_page: 10,
                relations: 'stroke',
            })

            const collectionModel = computed(() => <WorkoutCollectionModel>store.getters['workoutCollectionItem/get'])
            const workouts = computed(() => <WorkoutList>store.getters['workoutCollection/get'])

            const capture = () => {
                store.dispatch('workoutCollectionItem/get', { id: id, payload: { relations: 'strokes' } }).then(() => {
                    state.overlay = false
                })
            }

            const fetchWorkouts = () => {
                state.workoutsBusy = true
                store.dispatch('workoutCollection/get', workoutQueries).then((data: any) => {
                    state.workoutsMeta = data.meta
                    state.workoutsBusy = false
                })
            }

            // The table is paginated, so it cannot tell whether everything is hidden — count separately.
            // `ai=0` is not a filter (only `ai=1` is), so the restorable ones are all hidden minus hidden AI.
            const countInLibrary = () => {
                const count = (queries: any) => useWorkoutHttp()
                    .get({ collection_id: id, per_page: 1, ...queries })
                    .then((data: any) => <number>data.meta.total)
                count({ library_hidden: 0 }).then((total) => {
                    state.inLibrary = total
                })
                Promise.all([count({ library_hidden: 1 }), count({ library_hidden: 1, ai: 1 })]).then(([hidden, ai]) => {
                    state.restorable = hidden - ai
                })
            }

            const onWorkoutsPage = (updatedQueries: any) => {
                workoutQueries.page = updatedQueries.page
                fetchWorkouts()
            }

            const suspend = (model: WorkoutCollectionModel) => {
                state.overlay = true
                store.dispatch('workoutCollectionItem/update', {
                    id: model.id,
                    payload: { enabled: ! model.enabled }
                }).then(() => {
                    store.dispatch('notify', { color: 'success', message: i18n.t('general_notice_data_updated') })
                    capture()
                })
            }

            const hideWorkouts = (model: WorkoutCollectionModel) => {
                state.overlay = true
                useWorkoutCollectionHttp().hideWorkouts(model.id).then((data: any) => {
                    store.dispatch('notify', {
                        color: 'success',
                        message: i18n.t('collection_notice_workouts_hidden', { hidden: data.data.hidden, workouts: data.data.workouts }),
                    })
                    state.overlay = false
                    fetchWorkouts()
                    countInLibrary()
                }).catch(() => {
                    state.overlay = false
                })
            }

            const showWorkouts = (model: WorkoutCollectionModel) => {
                state.overlay = true
                useWorkoutCollectionHttp().showWorkouts(model.id).then((data: any) => {
                    store.dispatch('notify', {
                        color: 'success',
                        message: i18n.t('collection_notice_workouts_shown', { shown: data.data.shown, workouts: data.data.workouts }),
                    })
                    state.overlay = false
                    fetchWorkouts()
                    countInLibrary()
                }).catch(() => {
                    state.overlay = false
                })
            }

            // One workout at a time, the same call as the workouts list's Hide / Show in library action.
            const toggleLibraryHidden = (workout: Workout) => {
                state.workoutsBusy = true
                useWorkoutHttp().update(workout.id, { library_hidden: ! workout.libraryHidden, only: 'library_hidden' }).then(() => {
                    fetchWorkouts()
                    countInLibrary()
                }).catch(() => {
                    state.workoutsBusy = false
                })
            }

            const edit = () => router.push({ name: 'workout-collections.show', params: { id: <any>collectionModel.value.id } })

            const del = (model: WorkoutCollectionModel) => {
                state.overlay = true
                store.dispatch('workoutCollectionItem/delete', { id: model.id }).then(() => {
                    router.push({ name: 'workout-collections' })
                })
            }

            // One row per attitude that actually has characteristics; hidden entirely when audience is empty.
            const audienceItems = () => {
                const audience = collectionModel.value.audience || {}
                return enums.audienceAttitudes()
                    .map((attitude: any) => ({
                        text: attitude.text,
                        characteristics: Object.keys(audience).filter(key => audience[key] === attitude.value),
                    }))
                    .filter((item: any) => item.characteristics.length > 0)
                    .map((item: any) => ({
                        text: item.text,
                        value: enums.audienceCharacteristicsStrTitle(item.characteristics),
                    }))
            }

            // SSO-3654: the "only show to" gate, shown only when set.
            const onlyShowToItems = () => {
                const gate = collectionModel.value.audienceOnlyShowTo || []
                return gate.length
                    ? [{ text: i18n.t('collection_audience_only_show_to'), value: enums.audienceCharacteristicsStrTitle(gate) }]
                    : []
            }

            const items = () => {
                return <any>[
                    ...audienceItems(),
                    ...onlyShowToItems(),
                    { text: i18n.t('collection_label_level'), value: enums.levelTitle(collectionModel.value.level) },
                    { text: i18n.t('collection_label_strokes'), value: strokeEnums.strokesStrTitle(collectionModel.value.strokeIds) },
                    { text: i18n.t('collection_label_workouts'), value: collectionModel.value.workoutCount },
                    { text: i18n.t('collection_label_sort_order'), value: collectionModel.value.sortOrder },
                    { text: i18n.t('general_label_creation_date'), value: collectionModel.value.createdAt.format('DD/MM/YYYY') },
                    { text: i18n.t('general_action_published'), value: collectionModel.value.enabled ? i18n.t('general_text_yes') : i18n.t('general_text_no') },
                ]
            }

            onMounted(() => {
                workouts.value.data = []
                capture()
                fetchWorkouts()
                countInLibrary()
            })

            return {
                capture,
                items,
                time,
                collection: collectionModel.value,
                workouts: workouts.value,
                onWorkoutsPage,
                edit,
                del,
                suspend,
                hideWorkouts,
                showWorkouts,
                toggleLibraryHidden,
                ...useI18n(),
                ...useEnums(),
                ...toRefs(state)
            }
        }
    })
</script>
